/**
 * People — everyone connected to this academy, and what each of them can do.
 *
 * ONE MODULE, NOT TWO. "Team" and "Learners" were two pages over one dataset, and the
 * split does not survive contact with a real academy: an instructor is usually enrolled
 * in a colleague's course, so the same person belongs on both lists and the two screens
 * disagree about who they are. Here they are one row with a filter above it, and both
 * sidebar entries land in this module with that filter preset.
 *
 * WHY EACH PERSON APPEARS IS SHOWN. The list is the union of three independent signals
 * — registered on this site, holds a role in its orgs, actively enrolled in one of its
 * courses — because any one of them alone is wrong. Registration alone, which is what
 * this page used to show, missed everyone an administrator had added and everyone put
 * on a course team: on the install this was measured against it showed one of the two
 * people actually working in the academy. `sources` says which signal applies, so an
 * unfamiliar name is never a mystery.
 *
 * PLATFORM OPERATORS ARE NOT ON THE LIST, and the API is what leaves them off — so the
 * counts, the tabs and the table all agree without any of them having to know about it.
 * A superuser is not a member of an academy: they hold no membership here, every write
 * against them is refused, and naming them would tell each tenant who operates the
 * platform. What they can do is unchanged; who this page is about is.
 *
 * Four decisions carried over from the Team page, all consequences of what the API can
 * and cannot do:
 *
 * ADDING SOMEONE NEVER CREATES AN ACCOUNT. The server looks up an existing account and
 * 404s when there is none, because minting one would produce a user with no password
 * and no way in. So "no such account" is a designed outcome with its own explanation
 * and a next step, not an error banner.
 *
 * ROLE NAMES ARE NOT SHOWN. Underneath, an administrator is `OrgInstructorRole` and
 * staff is `OrgStaffRole` — a pair whose names imply the wrong ranking to everyone who
 * meets them. The page names the job instead and always spells out what the level can
 * do before it is applied.
 *
 * PROMOTION TO ADMINISTRATOR IS CONFIRMED. It hands over authority across the whole
 * academy, including over the person granting it. Demotions are not confirmed here: the
 * server refuses the only two that cannot be undone from inside the academy (yourself,
 * and the last administrator), so everything the UI still offers is reversible.
 *
 * REMOVAL IS ACADEMY-SCOPED, AND IS NOT UNENROLLING. It revokes this academy's roles
 * and deletes the membership row; the account, its enrolments, its grades and its
 * certificates all survive. Someone who is enrolled here therefore STAYS on this list
 * afterwards, as a learner — the confirmation says so, because a destructive-looking
 * button that leaves the person in place reads as broken otherwise.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  Dialog,
  Field,
  Icon,
  Kpi,
  KpiRow,
  SearchField,
  SelectField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import { ANONYMOUS, loadSession, personaOf } from '../../shared/lib/session';
import {
  PERSONA_BLURBS,
  PERSONA_ORDER,
  SOURCE_BLURBS,
  SOURCE_ORDER,
  addMember,
  canRemove,
  isTeam,
  listMembers,
  memberError,
  personaLabel,
  removeMember,
  setMemberPersona,
  sourceLabel,
  type Member,
  type MemberPersona,
  type MemberSource,
} from '../../shared/lib/team';
import { PageHeader } from '../AdminShell';
// The marker, per-row access picker, legend and dialog rhythm were built for this
// module when it was the Team page. Imported rather than copied so there is one
// definition of them; `people.css` holds only what the merge added.
import '../routes/admin-team.css';
import './people.css';

/** Options for every persona picker, highest authority first. */
const PERSONA_OPTIONS: [string, string][] = PERSONA_ORDER.map((persona) => [
  persona,
  personaLabel(persona),
]);

/**
 * Which slice of the academy is on screen.
 *
 * `team` is anyone with authority here (administrator, staff, or a platform operator);
 * `learners` is everyone else. Deliberately coarser than the access-level filter beside
 * it — this answers "who am I looking at", that one answers "exactly what are they".
 */
export type PeopleFilter = 'everyone' | 'team' | 'learners';

const TITLES: Record<PeopleFilter, { title: string; description: string }> = {
  everyone: {
    title: 'People',
    description:
      'Everyone connected to this academy — the people who run it, teach in it and learn in it.',
  },
  team: {
    title: 'Team',
    description: 'The people who run this academy and teach in it, and what each of them can do.',
  },
  learners: {
    title: 'Learners',
    description: 'Everyone here who is taking courses rather than running them.',
  },
};

export const personHref = (username: string) => `/admin/people/${encodeURIComponent(username)}`;

export function joinedOn(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

/**
 * Why this person is on the list.
 *
 * Shown on every row rather than behind a tooltip: the whole point of merging the two
 * pages is that "team" and "learner" stopped being where someone comes from, so the
 * real answer has to be visible.
 */
export function SourceMarks({ sources }: { sources: MemberSource[] }) {
  if (sources.length === 0) {
    return <span className="admin__sub">—</span>;
  }
  return (
    <span className="people__sources">
      {SOURCE_ORDER.filter((source) => sources.includes(source)).map((source) => (
        <span key={source} className="people__source" data-source={source} title={SOURCE_BLURBS[source]}>
          {sourceLabel(source)}
        </span>
      ))}
    </span>
  );
}

export default function AdminPeople({ preset = 'everyone' }: { preset?: PeopleFilter }) {
  const config = getConfig();
  const slug = config.tenant.slug;
  const queryClient = useQueryClient();

  // The session decides what this page OFFERS; the API decides what it allows. Fetched
  // here rather than passed down, because admin routes render under a shared outlet.
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const persona = personaOf(session.data ?? ANONYMOUS, config.tenant.orgs);
  const canManage = persona === 'tenant_admin' || persona === 'superadmin';
  const me = session.data?.user?.username ?? '';

  // Preset only seeds the tab: arriving from "Team" should show the team, and switching
  // to everyone should not need a second page.
  const [tab, setTab] = useState<PeopleFilter>(preset);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [promoting, setPromoting] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['tenant-members', slug],
    queryFn: () => listMembers(slug),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tenant-members', slug] });

  const change = useMutation({
    mutationFn: ({ username, persona: next }: { username: string; persona: MemberPersona }) =>
      setMemberPersona(slug, username, next),
    onSuccess: (member) => {
      setPromoting(null);
      setNotice(`${member.name} is now ${personaLabel(member.persona).toLowerCase()}.`);
      void refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (member: Member) => removeMember(slug, member.username),
    onSuccess: (_data, member) => {
      setRemoving(null);
      setNotice(
        member.enrolmentCount > 0
          ? `${member.name} no longer has access to this academy. They are still enrolled in ${member.enrolmentCount === 1 ? 'a course' : `${member.enrolmentCount} courses`} here, so they remain listed as a learner.`
          : `${member.name} no longer has access to this academy. Their account and their learning are untouched.`,
      );
      void refresh();
    },
  });

  const all = members.data ?? [];

  const counts = useMemo(
    () => ({
      everyone: all.length,
      team: all.filter((m) => isTeam(m.persona)).length,
      learners: all.filter((m) => !isTeam(m.persona)).length,
      admins: all.filter((m) => m.persona === 'tenant_admin').length,
      staff: all.filter((m) => m.persona === 'tenant_staff').length,
      enrolled: all.filter((m) => m.enrolmentCount > 0).length,
    }),
    [all],
  );

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return all.filter((member) => {
      if (tab === 'team' && !isTeam(member.persona)) return false;
      if (tab === 'learners' && isTeam(member.persona)) return false;
      if (filter && member.persona !== filter) return false;
      if (!needle) return true;
      return (
        member.name.toLowerCase().includes(needle) ||
        member.username.toLowerCase().includes(needle) ||
        member.email.toLowerCase().includes(needle)
      );
    });
  }, [all, search, filter, tab]);

  /**
   * Whose access may be edited from here.
   *
   * Not yourself — the server refuses it, because removing or demoting the account you
   * are signed in as is how an academy loses its last administrator. Not a platform
   * administrator either: their authority comes from the platform, so changing roles
   * here would appear to work and do nothing. The API no longer lists them at all, so
   * that second clause is a backstop rather than a case anyone should meet.
   */
  const editable = (member: Member) =>
    canManage && member.username !== me && member.persona !== 'superadmin';

  const columns: Column<Member>[] = useMemo(
    () => [
      {
        key: 'person',
        header: 'Person',
        render: (member) => (
          <>
            <Link className="people__name" to={personHref(member.username)}>
              {member.name}
            </Link>
            {member.username === me && <span className="team__you">You</span>}
            <div className="admin__sub mono">{member.username}</div>
          </>
        ),
      },
      { key: 'email', header: 'Email', render: (member) => member.email || '—' },
      {
        key: 'persona',
        header: 'Access',
        render: (member) =>
          editable(member) ? (
            <label className="team__persona">
              <span className="team__persona-label">Access level for {member.name}</span>
              <select
                className="admin__select"
                value={member.persona}
                aria-label={`Access level for ${member.name}`}
                disabled={change.isPending}
                onChange={(event) => {
                  const next = event.target.value as MemberPersona;
                  if (next === member.persona) return;
                  // Promotion hands over authority over the whole academy, so it is
                  // confirmed. Everything else the server lets through is reversible.
                  if (next === 'tenant_admin') {
                    setNotice(null);
                    change.reset();
                    setPromoting(member);
                  } else {
                    setNotice(null);
                    change.mutate({ username: member.username, persona: next });
                  }
                }}
              >
                {PERSONA_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <Badge tone={member.persona === 'tenant_admin' ? 'info' : 'neutral'}>
              {personaLabel(member.persona)}
            </Badge>
          ),
      },
      {
        key: 'sources',
        header: 'Appears because',
        render: (member) => <SourceMarks sources={member.sources} />,
      },
      {
        key: 'enrolments',
        header: 'Enrolments',
        numeric: true,
        render: (member) => (
          <>
            {member.enrolmentCount || '—'}
            {member.staffCourses.length > 0 && (
              <div className="admin__sub">
                teaches {member.staffCourses.length}
              </div>
            )}
          </>
        ),
      },
      {
        key: 'status',
        header: 'Account',
        render: (member) =>
          member.isActive ? (
            <Badge tone="success">Active</Badge>
          ) : (
            <Badge tone="warning">Not activated</Badge>
          ),
      },
      { key: 'joined', header: 'Joined', render: (member) => joinedOn(member.joined) },
      {
        key: 'actions',
        header: 'Actions',
        render: (member) =>
          editable(member) && canRemove(member) ? (
            <div className="admin__row-actions">
              <Button
                variant="ghost"
                onClick={() => {
                  setNotice(null);
                  remove.reset();
                  setRemoving(member);
                }}
              >
                Remove
              </Button>
            </div>
          ) : (
            /* Someone here only because they are enrolled has no access to revoke, so
               there is nothing "Remove" could do to them. */
            <span className="admin__sub">—</span>
          ),
      },
    ],
    [me, canManage, change.isPending],
  );

  const listFailure = memberError(members.error);
  const heading = TITLES[tab];

  return (
    <>
      <PageHeader
        title={heading.title}
        description={heading.description}
        actions={
          canManage && (
            <Button
              variant="primary"
              onClick={() => {
                setNotice(null);
                setAdding(true);
              }}
            >
              <Icon name="person_add" />
              Add person
            </Button>
          )
        }
      />

      {!canManage && !session.isLoading && (
        <Banner tone="info">
          <strong>You can see who is here, but not change it.</strong> Only an
          administrator can add people, change what they can do, or remove them.
        </Banner>
      )}

      {notice && <Banner tone="success">{notice}</Banner>}

      {/* Failures from the row controls surface here: the row itself is too small to
          hold a sentence, and these refusals are sentences worth reading. */}
      {change.isError && <ActionError error={change.error} what="That change was not applied." />}
      {remove.isError && !removing && (
        <ActionError error={remove.error} what="That person was not removed." />
      )}

      {members.isLoading || session.isLoading ? (
        <Spinner label="Loading people…" />
      ) : members.isError ? (
        <Banner tone="danger">
          {listFailure.status === 403 ? (
            <>
              <strong>You do not have access to this academy&rsquo;s people.</strong> Ask an
              administrator here to give you access.
            </>
          ) : (
            <>
              <strong>Could not load the people in this academy.</strong>{' '}
              {listFailure.detail ??
                'The platform did not answer. Nothing has changed — retry in a moment.'}
            </>
          )}
        </Banner>
      ) : (
        <>
          <KpiRow>
            <Kpi label="Administrators" value={counts.admins} hint="Full control of the academy" />
            <Kpi label="Staff" value={counts.staff} hint="Build courses and teach" />
            <Kpi label="Learners" value={counts.learners} hint="Take courses only" />
            <Kpi
              label="Taking a course"
              value={counts.enrolled}
              hint="Enrolled here, whatever else they do"
            />
          </KpiRow>

          <Toolbar>
            <Tabs
              label="Who to show"
              active={tab}
              onChange={(id) => {
                setTab(id as PeopleFilter);
                // The access-level filter can only contradict the tab it lands in —
                // "Learners" plus "Administrator" is an empty list that looks like a bug.
                setFilter('');
              }}
              tabs={[
                { id: 'everyone', label: 'Everyone', count: counts.everyone },
                { id: 'team', label: 'Team', count: counts.team },
                { id: 'learners', label: 'Learners', count: counts.learners },
              ]}
            />
            <div className="toolbar__end">
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Search by name, username or email…"
              />
              {tab !== 'learners' && (
                <select
                  className="admin__select"
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  aria-label="Filter by access level"
                >
                  <option value="">Every access level</option>
                  {PERSONA_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </Toolbar>

          <Card>
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(member) => member.username}
              empty={
                all.length === 0 ? (
                  <>
                    <strong>Nobody is connected to this academy yet.</strong>
                    <p>
                      {canManage
                        ? 'People appear here as soon as they register on this academy, are given a role, or are enrolled in one of its courses.'
                        : 'Nobody has registered, been given a role, or been enrolled here yet.'}
                    </p>
                  </>
                ) : (
                  <>
                    <strong>Nobody matches that.</strong>
                    <p>Try a different name, another tab, or clear the access-level filter.</p>
                  </>
                )
              }
            />
          </Card>

          <div className="admin__cols">
            <Card title="What each access level can do">
              <dl className="team__legend">
                {PERSONA_ORDER.map((value) => (
                  <div key={value}>
                    <dt>{personaLabel(value)}</dt>
                    <dd>{PERSONA_BLURBS[value]}</dd>
                  </div>
                ))}
              </dl>
            </Card>

            <Card title="Why people appear here">
              <dl className="team__legend">
                {SOURCE_ORDER.map((source) => (
                  <div key={source}>
                    <dt>{sourceLabel(source)}</dt>
                    <dd>{SOURCE_BLURBS[source]}</dd>
                  </div>
                ))}
              </dl>
              <p className="team__quiet people__note">
                Someone who is here only because they are enrolled has no access to take
                away, so they cannot be removed from the academy. Ending an enrolment is a
                different action and is not built yet.
              </p>
            </Card>
          </div>
        </>
      )}

      {adding && (
        <AddMemberDialog
          slug={slug}
          onClose={() => setAdding(false)}
          onAdded={(member, created) => {
            setAdding(false);
            setNotice(
              created
                ? `${member.name} was added as ${personaLabel(member.persona).toLowerCase()}.`
                : `${member.name} was already here. Their access is now ${personaLabel(member.persona).toLowerCase()}.`,
            );
            void refresh();
          }}
        />
      )}

      {promoting && (
        <Dialog
          title="Make this person an administrator?"
          onClose={() => setPromoting(null)}
          footer={
            <>
              <Button
                variant="primary"
                busy={change.isPending}
                onClick={() =>
                  change.mutate({ username: promoting.username, persona: 'tenant_admin' })
                }
              >
                Make administrator
              </Button>
              <Button variant="ghost" onClick={() => setPromoting(null)}>
                Cancel
              </Button>
            </>
          }
        >
          <p>
            <strong>{promoting.name}</strong> will be able to do everything you can:
            add and remove people, change what anyone here can do — including you — and
            edit this academy&rsquo;s settings.
          </p>
          <p className="team__quiet">
            They keep everything they can do today. You can change them back later, as
            long as somebody else is still an administrator.
          </p>
          {change.isError && <ActionError error={change.error} what="Nothing was changed." />}
        </Dialog>
      )}

      {removing && (
        <RemoveDialog
          member={removing}
          busy={remove.isPending}
          error={remove.isError ? remove.error : null}
          onClose={() => setRemoving(null)}
          onConfirm={() => remove.mutate(removing)}
        />
      )}
    </>
  );
}

/* --------------------------------------------------------- remove from academy ---- */

/**
 * The one destructive action here, and the one most easily misread.
 *
 * "Remove" next to a person's name reads as "delete this human", and for anyone who has
 * used a course tool it also reads as "unenrol them". It is neither. The body says what
 * goes (this academy's access), what stays (the account and every piece of learning),
 * and — when they are enrolled — that they will still be on this list afterwards,
 * because a confirmation that leaves the row in place is how people press it twice.
 */
export function RemoveDialog({
  member,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  member: Member;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <ConfirmDialog
      title={`Remove ${member.name} from this academy?`}
      confirmLabel="Remove from academy"
      busy={busy}
      onClose={onClose}
      onConfirm={onConfirm}
      body={
        <>
          <p>
            They lose access to this academy: the roles they hold here are revoked, along
            with the staff tools that come with them.
          </p>
          <p>
            <strong>Their account is not deleted, and nothing they have learned is
            touched.</strong> They keep their sign-in, their enrolments, their grades and
            their certificates — here and in any other academy they belong to. Add them
            again at any time and their learning is still there.
          </p>
          {member.enrolmentCount > 0 && (
            <p>
              <strong>This does not unenrol them.</strong> They are taking{' '}
              {member.enrolmentCount === 1
                ? 'one of this academy’s courses'
                : `${member.enrolmentCount} of this academy’s courses`}
              , so they will still appear on this list afterwards — as a learner, marked{' '}
              &ldquo;{sourceLabel('enrolment')}&rdquo;. Ending an enrolment is a separate
              action and is not built yet.
            </p>
          )}
          {error !== null && <ActionError error={error} what="They were not removed." />}
        </>
      }
    />
  );
}

/* ----------------------------------------------------------------- add a person ---- */

/**
 * Add someone who already has an account.
 *
 * Deliberately not an invitation form. The server will not create accounts, so a field
 * that accepted any address would promise something the platform does not do; when the
 * address is unknown the dialog says who has to act next instead of showing a failure.
 */
function AddMemberDialog({
  slug,
  onClose,
  onAdded,
}: {
  slug: string;
  onClose: () => void;
  onAdded: (member: Member, created: boolean) => void;
}) {
  const [identifier, setIdentifier] = useState('');
  const [persona, setPersona] = useState<MemberPersona>('tenant_staff');

  const add = useMutation({
    mutationFn: () => addMember(slug, identifier, persona),
    onSuccess: (member) => onAdded(member, member.created),
  });

  const failure = memberError(add.error);
  const ready = identifier.trim().length > 0;

  return (
    <Dialog
      title="Add someone to this academy"
      onClose={onClose}
      footer={
        <>
          <Button
            variant="primary"
            busy={add.isPending}
            disabled={!ready}
            onClick={() => add.mutate()}
          >
            Add to academy
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      {/* Own layout class rather than the console's: `console__form` is styled in
          console.css, which only the platform console loads. */}
      <form
        className="team__form"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) add.mutate();
        }}
      >
        <Field
          label="Email address"
          name="member-identifier"
          type="text"
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          hint="The address they registered with. A username works too."
          autoFocus
          required
        />

        <SelectField
          label="What they can do here"
          name="member-persona"
          value={persona}
          onChange={(event) => setPersona(event.target.value as MemberPersona)}
          options={PERSONA_OPTIONS}
        />
        <p className="team__blurb">{PERSONA_BLURBS[persona]}</p>

        {add.isError &&
          (failure.code === 'no_such_account' ? (
            /* The most common outcome by far, and not a fault: this is the one thing
               this screen cannot do for the admin. */
            <Banner tone="warning">
              <strong>Nobody has registered with that address yet.</strong> Adding someone
              here does not create an account or send an invitation. Ask them to register
              at this academy first, then add them — it takes a moment and their access is
              ready immediately.
            </Banner>
          ) : (
            <ActionError error={add.error} what="Nobody was added." />
          ))}

        <p className="team__quiet">
          You can change this or remove them at any time. Nothing is emailed to them.
        </p>
      </form>
    </Dialog>
  );
}

/* ---------------------------------------------------------------------- errors ---- */

/**
 * One failure, in the server's own words where it has them.
 *
 * The member API answers refusals with a sentence written for the reader — "X is the
 * only administrator of this academy…" — so it is repeated verbatim rather than
 * translated from a status code. Only the fallbacks are ours.
 */
export function ActionError({ error, what }: { error: unknown; what: string }) {
  const { status, detail } = memberError(error);

  if (detail) {
    return (
      <Banner tone="danger">
        <strong>{detail}</strong> {what}
      </Banner>
    );
  }
  if (status === 403) {
    return (
      <Banner tone="danger">
        <strong>You are not an administrator of this academy.</strong> {what} Ask an
        administrator here to make the change.
      </Banner>
    );
  }
  return (
    <Banner tone="danger">
      <strong>{what}</strong>{' '}
      {status
        ? `The platform returned HTTP ${status}. Nothing was changed, so it is safe to try again.`
        : 'The platform could not be reached. Nothing was changed, so it is safe to try again.'}
    </Banner>
  );
}
